import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  updateDoc,
  deleteDoc,
  Timestamp,
  addDoc
} from 'firebase/firestore';
import { db } from '../config';
import { SecurityService } from './securityService';

export interface License {
  id?: string;
  userId: string;
  licenseKey: string; // Added license key field
  licenseType: 'social-pro' | 'layout-pro' | 'ecommerce-pro' | 'complete';
  status: 'active' | 'expired' | 'suspended';
  purchaseDate: Timestamp;
  expiryDate: Timestamp;
  sites: string[];
  maxSites: number;
  price: number;
  transactionId?: string;
}

export interface UserProfile {
  id: string;
  email: string;
  displayName?: string;
  createdAt: Timestamp;
  totalLicenses: number;
  activeLicenses: number;
}

// License Service
export class LicenseService {
  
  // Verify license with external endpoint
  static async verifyLicense(
    licenseKey: string, 
    domain: string, 
    additionalData?: {
      userEmail?: string;
      pluginVersion?: string;
      pluginIdentifier?: string;
      installationId?: string;
      licenseType?: string;
      autoAddDomain?: boolean;
      requestedCategory?: string;
    }
  ): Promise<{
    isValid: boolean;
    message?: string;
    licenseType?: string;
    expiryDate?: string;
    userEmail?: string;
    maxSites?: number;
    currentSites?: number;
    accessibleCategories?: string[];
    requestedCategory?: string;
  }> {
    try {
      const requestBody: any = {
        licenseKey,
        domain
      };

      // Add additional verification data if provided
      if (additionalData) {
        if (additionalData.userEmail) requestBody.userEmail = additionalData.userEmail;
        if (additionalData.pluginVersion) requestBody.pluginVersion = additionalData.pluginVersion;
        if (additionalData.pluginIdentifier) requestBody.pluginIdentifier = additionalData.pluginIdentifier;
        if (additionalData.installationId) requestBody.installationId = additionalData.installationId;
        if (additionalData.licenseType) requestBody.licenseType = additionalData.licenseType;
        if (additionalData.autoAddDomain !== undefined) requestBody.autoAddDomain = additionalData.autoAddDomain;
        if (additionalData.requestedCategory) requestBody.requestedCategory = additionalData.requestedCategory;
      }

      const response = await fetch('https://us-central1-lava-addons-licensing.cloudfunctions.net/verifyLicense', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error verifying license:', error);
      throw error;
    }
  }

  // Generate a unique license key
  static generateLicenseKey(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 16; i++) {
      if (i > 0 && i % 4 === 0) {
        result += '-';
      }
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  // Create a new license
  static async createLicense(licenseData: Omit<License, 'id'>): Promise<string> {
    try {
      // Security validation
      SecurityService.validateUserAccess(licenseData.userId);
      SecurityService.validateLicenseData(licenseData);
      SecurityService.checkRateLimit(licenseData.userId, 'create_license');
      
      // Generate license key if not provided
      const licenseWithKey = {
        ...licenseData,
        licenseKey: licenseData.licenseKey || this.generateLicenseKey()
      };
      
      const docRef = await addDoc(collection(db, 'licenses'), {
        ...licenseWithKey,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
      
      SecurityService.logSecurityEvent('license_created', licenseData.userId, {
        licenseType: licenseData.licenseType,
        price: licenseData.price
      });
      
      return docRef.id;
    } catch (error: any) {
      console.error('Error creating license:', error);
      SecurityService.logSecurityEvent('license_creation_failed', licenseData.userId, { error: error instanceof Error ? error.message : 'Unknown error' });
      throw error;
    }
  }

  // Get all licenses for a user
  static async getUserLicenses(userId: string): Promise<License[]> {
    try {
      const q = query(
        collection(db, 'licenses'),
        where('userId', '==', userId)
      );
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as License[];
    } catch (error) {
      console.error('Error getting user licenses:', error);
      throw error;
    }
  }

  // Get a specific license
  static async getLicense(licenseId: string): Promise<License | null> {
    try {
      const docRef = doc(db, 'licenses', licenseId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        } as License;
      }
      return null;
    } catch (error) {
      console.error('Error getting license:', error);
      throw error;
    }
  }

  // Update a license
  static async updateLicense(licenseId: string, updates: Partial<License>): Promise<void> {
    try {
      const docRef = doc(db, 'licenses', licenseId);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error updating license:', error);
      throw error;
    }
  }

  // Add a site to a license
  static async addSiteToLicense(licenseId: string, domain: string): Promise<void> {
    let license: License | null = null;
    try {
      license = await this.getLicense(licenseId);
      if (!license) {
        throw new Error('License not found');
      }

      // Security validation
      SecurityService.validateUserAccess(license.userId);
      SecurityService.checkRateLimit(license.userId, 'add_site');
      
      // Sanitize domain
      const cleanDomain = SecurityService.sanitizeDomain(domain);

      if (license.sites.length >= license.maxSites) {
        throw new Error('License has reached maximum number of sites');
      }

      if (license.sites.includes(cleanDomain)) {
        throw new Error('Site already added to this license');
      }

      await this.updateLicense(licenseId, {
        sites: [...license.sites, cleanDomain]
      });
      
      SecurityService.logSecurityEvent('site_added', license.userId, {
        licenseId,
        domain: cleanDomain
      });
    } catch (error: any) {
      console.error('Error adding site to license:', error);
      SecurityService.logSecurityEvent('site_add_failed', license?.userId || 'unknown', { error: error instanceof Error ? error.message : 'Unknown error' });
      throw error;
    }
  }

  // Remove a site from a license
  static async removeSiteFromLicense(licenseId: string, domain: string): Promise<void> {
    try {
      const license = await this.getLicense(licenseId);
      if (!license) {
        throw new Error('License not found');
      }

      const updatedSites = license.sites.filter(site => site !== domain);
      await this.updateLicense(licenseId, {
        sites: updatedSites
      });
    } catch (error) {
      console.error('Error removing site from license:', error);
      throw error;
    }
  }

  // Check if a domain is licensed
  static async isDomainLicensed(domain: string, userId: string): Promise<boolean> {
    try {
      const licenses = await this.getUserLicenses(userId);
      return licenses.some(license => 
        license.status === 'active' && 
        license.sites.includes(domain)
      );
    } catch (error) {
      console.error('Error checking domain license:', error);
      throw error;
    }
  }

  // Get license statistics for a user
  static async getUserLicenseStats(userId: string): Promise<{
    totalLicenses: number;
    activeLicenses: number;
    totalSites: number;
    availableSites: number;
  }> {
    try {
      const licenses = await this.getUserLicenses(userId);
      
      const totalLicenses = licenses.length;
      const activeLicenses = licenses.filter(l => l.status === 'active').length;
      const totalSites = licenses.reduce((sum, l) => sum + l.sites.length, 0);
      const availableSites = licenses.reduce((sum, l) => sum + (l.maxSites - l.sites.length), 0);

      return {
        totalLicenses,
        activeLicenses,
        totalSites,
        availableSites
      };
    } catch (error) {
      console.error('Error getting license stats:', error);
      throw error;
    }
  }
} 